import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AuthInline } from "@/components/auth/AuthInline";
import { supabase } from "@/integrations/supabase/client";
import { formatSiteDateRange } from "@/lib/datetime.schedule";
import { registerForEvent } from "@/lib/event-registration.functions";
import { listPublishedEvents } from "@/lib/events.functions";
import { englishOnlyContentLang } from "@/lib/locale";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime";

type SiteEvent = Awaited<ReturnType<typeof listPublishedEvents>>[number];

export function EventRegistrationDialog({
  event,
  open,
  onClose,
}: {
  event: SiteEvent | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [authed, setAuthed] = useState(false);
  const [form, setForm] = useState({
    registrantName: "",
    registrantAge: "",
    phone: "",
    notes: "",
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setAuthed(!!s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!open || !authed) return;
    supabase.auth.getUser().then(({ data }) => {
      const meta = data.user?.user_metadata;
      setForm((prev) => ({
        ...prev,
        registrantName: prev.registrantName || (meta?.full_name as string) || "",
        phone: prev.phone || (meta?.phone as string) || "",
      }));
    });
  }, [open, authed]);

  const registerMut = useMutation({
    mutationFn: () =>
      registerForEvent({
        data: {
          eventId: event!.id,
          registrantName: form.registrantName.trim(),
          registrantAge: form.registrantAge ? Number(form.registrantAge) : null,
          phone: form.phone.trim(),
          notes: form.notes.trim() || null,
        },
      }),
    onSuccess: () => {
      toast.success(m.events_toast_registered());
      qc.invalidateQueries({ queryKey: ["site-events"] });
      qc.invalidateQueries({ queryKey: ["event-registration-counts"] });
      qc.invalidateQueries({ queryKey: ["my-event-registration-ids"] });
      qc.invalidateQueries({ queryKey: ["my-event-registrations"] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit = form.registrantName.trim() && form.phone.trim();

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        {event && (
          <>
            <DialogHeader>
              <DialogTitle
                lang={englishOnlyContentLang(getLocale())}
                className="font-display text-2xl"
              >
                {event.title}
              </DialogTitle>
              <DialogDescription>
                {formatSiteDateRange(event.start_at, event.end_at, getLocale())}
              </DialogDescription>
            </DialogHeader>

            {!authed ? (
              <AuthInline heading={m.events_auth_heading()} description={m.events_auth_desc()} />
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  registerMut.mutate();
                }}
              >
                <div>
                  <Label htmlFor="reg-name">{m.events_label_registrant()}</Label>
                  <Input
                    id="reg-name"
                    value={form.registrantName}
                    onChange={(e) => setForm({ ...form, registrantName: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg-age">{m.booking_label_age()}</Label>
                  <Input
                    id="reg-age"
                    type="number"
                    min={1}
                    max={120}
                    value={form.registrantAge}
                    onChange={(e) => setForm({ ...form, registrantAge: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="reg-phone">{m.booking_label_phone()}</Label>
                  <Input
                    id="reg-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="reg-notes">{m.booking_label_notes()}</Label>
                  <Textarea
                    id="reg-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={onClose}>
                    {m.common_cancel()}
                  </Button>
                  <Button
                    type="submit"
                    className="bg-ember-fill text-primary-foreground hover:bg-ember-fill/90"
                    disabled={registerMut.isPending || !canSubmit}
                  >
                    {registerMut.isPending ? m.events_registering() : m.events_register()}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
